import { makeAutoObservable, runInAction } from "mobx";
import {
  OsaNodeCoding,
  CreateOrUpdateOsaNodeCoding,
} from "../models/OsaNodeCoding";
import { OsaNodeApi } from "../../osaway/services/OsaNodeApi";
import {
  getAttributeValue,
  setAttributeByName,
} from "../utils/attribute-value";
import { CraAttributeName } from "@keita/lanepatrol-sdk";
import { OsaNodeCodingApi } from "../services/OsaNodeCodingApi";
import { RootStore } from "../../shared/stores/RootStore";
import { OsaWayApi } from "../../osaway/services/OsaWayApi";
import { CodingView } from "../models/CodingView";
import { LatestCodingChange } from "../models/LatestCodingChange";
import { SuggestionEngine } from "../utils/attribute-suggestions";
import { nodeReferences } from "../../osaway/stores/OsaNodeStore";

type osaNodeId = string;
type osaWayId = string;
const saveDelay = 30 * 1000; //ms

export class CodingStore {
  rootStore;
  suggestionEngine: SuggestionEngine;
  allNodeCodingsByWayId = new Map<osaWayId, Map<osaNodeId, OsaNodeCoding>>();
  dirtyNodeIds = new Set<osaNodeId>();

  view = CodingView.defaultValue;
  conflictWarning?: LatestCodingChange;
  savingChanges = false;

  private saveTimer?: NodeJS.Timer;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.suggestionEngine = new SuggestionEngine(rootStore);
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.allNodeCodingsByWayId = new Map();
      this.savingChanges = false;
      this.stopAutoSaveTimer();
      this.conflictWarning = undefined;
      this.dirtyNodeIds = new Set();
    });
  }

  getFromSelectedWay() {
    const { osaWayStore } = this.rootStore;
    if (!osaWayStore.selectedId) return undefined;
    return this.allNodeCodingsByWayId.get(osaWayStore.selectedId);
  }

  getFromSelectedNode() {
    const { osaNodeStore } = this.rootStore;
    if (!osaNodeStore.selectedId) return undefined;
    return this.getFromSelectedWay()?.get(osaNodeStore.selectedId);
  }

  get changesSaved() {
    return this.dirtyNodeIds.size === 0;
  }

  setView(view: CodingView.Enum) {
    runInAction(() => {
      this.view = view;
    });
  }

  private newMap() {
    runInAction(() => {
      this.allNodeCodingsByWayId = new Map(this.allNodeCodingsByWayId);
    });
  }

  async codeAttribute(
    attributeName: CraAttributeName,
    newValue: string | number | null,
  ) {
    const { osaNodeStore } = this.rootStore;
    var selectedNodeIds = osaNodeStore.selectedId
      ? [osaNodeStore.selectedId]
      : [];

    if (osaNodeStore.multiSelectedIds.size) {
      selectedNodeIds = [...osaNodeStore.multiSelectedIds];
    }

    selectedNodeIds.forEach((nodeId) => {
      this.setAttributeValue(nodeId, attributeName, newValue);
    });
  }

  setAttributeValue(
    nodeId: string,
    attributeName: CraAttributeName,
    newValue: string | number | null,
  ) {
    const codingEntry = this.getFromSelectedWay()?.get(nodeId);

    if (codingEntry) {
      codingEntry.attributes = setAttributeByName(
        codingEntry.attributes,
        attributeName,
        newValue,
      );

      const suggestions = new Set(codingEntry.suggestions);
      suggestions.delete(attributeName);
      codingEntry.suggestions = [...suggestions];

      this.markAsDirty(nodeId);
      if (this.view !== CodingView.Enum.Spreadsheet) {
        runInAction(() => {
          this.suggestionEngine.makeSuggestions(codingEntry);
        });
      }
      this.newMap();
    }
  }

  async suggestValue(
    nodeCoding: OsaNodeCoding,
    attributeName: CraAttributeName,
    newValue: string | number | null,
    overwrite: boolean,
  ) {
    const prevValue = getAttributeValue(attributeName, nodeCoding.attributes);

    if (prevValue !== newValue && (prevValue == null || overwrite)) {
      nodeCoding.attributes = setAttributeByName(
        nodeCoding.attributes,
        attributeName,
        newValue,
      );

      const suggestions = new Set(nodeCoding.suggestions);
      suggestions.add(attributeName);
      nodeCoding.suggestions = [...suggestions];

      this.newMap();
      this.markAsDirty(nodeCoding.osaNodeId);
    }
  }

  private setSavingChanges(value: boolean) {
    runInAction(() => {
      this.savingChanges = value;
    });
  }

  async saveChanges() {
    const { osaNodeStore } = this.rootStore;
    const batchRequest = this.buildSaveCodingRequests();

    if (!batchRequest.length) return;

    this.setSavingChanges(true);
    await OsaNodeCodingApi.batchUpdate(batchRequest)
      .then(({ data }) => {
        data.forEach((savedCoding) => {
          const nodeId = savedCoding.osaNodeId;
          const nodeRef = nodeReferences.get(nodeId)!;
          const nodeCoding = this.allNodeCodingsByWayId
            .get(nodeRef.osaWayId)!
            .get(nodeId)!;
          nodeCoding.id = savedCoding.id;
        });

        batchRequest.forEach((request) => {
          osaNodeStore.refreshNodeScore(request.osaNodeId);
        });
      })
      .finally(() => this.setSavingChanges(false));
  }

  async onUnloadSaveChanges() {
    const entryRequests = this.buildSaveCodingRequests();

    if (!entryRequests.length) return;

    await OsaNodeCodingApi.batchUpdate(entryRequests);
  }

  private buildSaveCodingRequests() {
    const entryRequests: CreateOrUpdateOsaNodeCoding[] = [];

    this.dirtyNodeIds.forEach((nodeId) => {
      const { osaWayId } = nodeReferences.get(nodeId)!;

      const codingEntry = this.allNodeCodingsByWayId
        .get(osaWayId)!
        .get(nodeId)!;

      this.resetDirty(codingEntry.osaNodeId);
      entryRequests.push({
        id: codingEntry.id,
        osaNodeId: codingEntry.osaNodeId,
        attributes: codingEntry.attributes,
        suggestions: codingEntry.suggestions,
      });
    });

    return entryRequests;
  }

  copyFromPreviousNode() {
    const { osaNodeStore } = this.rootStore;
    const nodeCodings = this.getFromSelectedWay();
    const selectedNodeRef = nodeReferences.get(osaNodeStore.selectedId!)!;
    const currNodeCoding = nodeCodings?.get(osaNodeStore.selectedId!)!;

    if (selectedNodeRef.index > 0) {
      const nodeList = osaNodeStore.nodesByWayId.get(selectedNodeRef.osaWayId)!;
      const prevNode = nodeList[selectedNodeRef.index - 1];
      const prevNodeCoding = nodeCodings?.get(prevNode.id)!;

      for (const attributeName of Object.values(CraAttributeName)) {
        const value = getAttributeValue(
          attributeName,
          prevNodeCoding.attributes,
        );
        if (value != null) {
          this.suggestValue(currNodeCoding, attributeName, value, false);
        }
      }
    }

    runInAction(() => {
      this.suggestionEngine.makeSuggestions(currNodeCoding);
    });
  }

  startAutoSaveTimer = () => {
    const callback = () => {
      this.saveChanges();
    };
    runInAction(() => {
      this.saveTimer = setInterval(callback, saveDelay);
    });
  };

  stopAutoSaveTimer = () => {
    if (this.saveTimer) {
      runInAction(() => {
        clearInterval(this.saveTimer);
        this.saveTimer = undefined;
      });
    }
  };

  private markAsDirty = (nodeId: string) => {
    runInAction(() => {
      this.dirtyNodeIds.add(nodeId);
    });
  };

  private resetDirty = (nodeId: string) => {
    this.dirtyNodeIds.delete(nodeId);
  };

  clearConflictWarning() {
    runInAction(() => {
      this.conflictWarning = undefined;
    });
  }

  async fetchAttributes(nodeId: string) {
    const response = await OsaNodeApi.listCodingEntries(nodeId);
    runInAction(() => {
      this.getFromSelectedWay()?.set(nodeId, response.data);
    });
    this.newMap();
  }

  async fetchAttributesByOsaWayId(osaWayId: string, cancelIf?: () => boolean) {
    const { data: codingEntries } = await OsaWayApi.listCodingEntries(osaWayId);
    if (cancelIf?.()) return;

    const wayCodingEntries = new Map();
    codingEntries.forEach((attributes) => {
      wayCodingEntries!.set(attributes.osaNodeId, attributes);
    });
    runInAction(() => {
      this.allNodeCodingsByWayId.set(osaWayId, wayCodingEntries);
    });
    this.newMap();
  }

  async fetchLatestCodingChange(osaWayId: string) {
    const response = await OsaWayApi.getLatestCodingChange(osaWayId);
    if (response.status === 204 || response.data.me) return;
    runInAction(() => {
      this.conflictWarning = response.data;
    });
  }
}
